import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { KeyRound, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";

// Public sign-in page for CREATORS. Creators are provisioned only by admins
// (there is intentionally no self-signup form) — this page just accepts
// username + password. Admins can also use it to reach the creator
// dashboard. On success we hand off to `/creator`.
export default function SignIn() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/creator-login", { username, password });
      await refresh?.();
      toast.success(`Welcome back, ${data.name || data.username}`);
      nav("/creator");
    } catch (ex) {
      const msg = ex?.response?.data?.detail || "Sign-in failed";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-5 py-20">
      <Seo title="Creator Sign In · SyndaFam" description="Sign in to upload content as a SyndaFam creator." />
      <div className="w-full max-w-md">
        <div className="font-mono text-[10px] tracking-[0.4em] text-[#E63946] mb-2">// CONTROL ROOM</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">CREATOR SIGN IN.</h1>
        <p className="font-mono text-xs text-white/50 mb-8">
          Provisioned creators only. Ask an admin to set you up if you don&apos;t have an account yet.
        </p>

        <form onSubmit={submit} className="bg-[#151515] border border-white/10 p-6 space-y-4">
          <div className="font-mono text-[10px] tracking-widest text-white/50 flex items-center gap-2">
            <KeyRound className="w-3.5 h-3.5" /> USERNAME + PASSWORD
          </div>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="USERNAME"
            autoComplete="username"
            className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#E63946] outline-none px-4 py-3 font-mono tracking-wider text-sm"
            data-testid="signin-username-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="PASSWORD"
            autoComplete="current-password"
            className="w-full bg-[#0A0A0A] border border-white/20 focus:border-[#E63946] outline-none px-4 py-3 font-mono tracking-wider text-sm"
            data-testid="signin-password-input"
          />
          {err && <div className="font-mono text-xs text-[#E63946]" data-testid="signin-error">{err}</div>}
          <button
            type="submit"
            disabled={busy || !username || !password}
            className="w-full inline-flex items-center justify-center gap-2 font-display tracking-wide text-lg bg-[#E63946] hover:bg-[#C92A36] disabled:opacity-50 text-white px-6 py-3 transition-colors"
            data-testid="signin-submit"
          >
            {busy ? "SIGNING IN…" : "SIGN IN"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>

        <div className="mt-6 font-mono text-[10px] tracking-widest text-white/30">
          // ADMIN ACCESS? GO TO <a href="/admin" className="text-[#FCA311] hover:underline">/admin</a>
        </div>
      </div>
    </div>
  );
}
